import {
	ChatRoom,
	ChatRoomMember,
	ChatNotDeliveredMessagesCount,
	CreateChatRoomRequest,
	AddUserToChatRoomRequest,
	MuteUserRequest,
	BanUserRequest,
	PromoteUserRequest,
} from '../../Types/admin/chat';
import { BizObject, ObjectType } from '../../Types/base';
import { ChatUser } from '../../Types/chat';
import { transformDateStringPropsToDate } from '../../Utils/dateUtils';
import { get, getQSFromObject, httpDelete, post } from '../common';

const chatEndpoint = 'api/chat';
const chatAdminEndpoint = 'api/chatadmin';

const mapChatRoom = (chatRoom: ChatRoom | null): ChatRoom | null => {
	if (!chatRoom) return null;
	return transformDateStringPropsToDate<ChatRoom, keyof ChatRoom>(chatRoom, ['OpenDateUTC', 'CloseDateUTC']);
};

const mapChatRoomMember = (member: ChatRoomMember | null): ChatRoomMember | null => {
	if (!member) return null;
	return transformDateStringPropsToDate<ChatRoomMember, keyof ChatRoomMember>(member, [
		'MutedUntil',
		'BannedUntil',
		'JoinedUTC',
		'LastSeenUTC',
	]);
};

function chatRoomArrayMapper(arr: ChatRoom[] | null) {
	if (!arr) return arr;
	return arr.map((room) => mapChatRoom(room)).filter((room) => room !== null) as ChatRoom[];
}

function chatRoomMemberArrayMapper(arr: ChatRoomMember[] | null) {
	if (!arr) return arr;
	return arr.map((member) => mapChatRoomMember(member)).filter((member) => member !== null) as ChatRoomMember[];
}

// Admin endpoints
export async function getAllChatRooms() {
	const endpoint = `${chatAdminEndpoint}/GetAllChatRooms`;
	const result = await get<ChatRoom[]>(endpoint);
	return chatRoomArrayMapper(result);
}

export async function getOneChatRoom(options: { id: number }) {
	const endpoint = `${chatAdminEndpoint}/GetOneChatRoom?${getQSFromObject(options)}`;
	const result = await get<ChatRoom>(endpoint);
	return mapChatRoom(result);
}

export async function createUpdateChatRoom(chatRoom: CreateChatRoomRequest | ChatRoom) {
	const endpoint = `${chatAdminEndpoint}/CreateUpdateChatRoom`;
	const result = await post<ChatRoom>(endpoint, chatRoom);
	return mapChatRoom(result || null);
}

export async function deleteChatRoom(options: { id: number }) {
	const endpoint = `${chatAdminEndpoint}/DeleteChatRoom?${getQSFromObject(options)}`;
	await httpDelete(endpoint);
}

export async function getChatRoomMembers(options: { chatRoomId: number }) {
	const endpoint = `${chatAdminEndpoint}/GetChatRoomMembers?${getQSFromObject(options)}`;
	const result = await get<ChatRoomMember[]>(endpoint);
	return chatRoomMemberArrayMapper(result);
}

export async function addUserToChatRoom(request: AddUserToChatRoomRequest) {
	const endpoint = `${chatAdminEndpoint}/AddUserToChatRoom`;
	const result = await post<ChatRoomMember>(endpoint, request);
	return mapChatRoomMember(result || null);
}

export async function updateChatRoomMember(member: ChatRoomMember) {
	const endpoint = `${chatAdminEndpoint}/UpdateChatRoomMember`;
	await post<boolean>(endpoint, member);
}

export async function removeUserFromChatRoom(options: { chatRoomId: number; userId: number }) {
	const endpoint = `${chatAdminEndpoint}/RemoveUserFromChatRoom?${getQSFromObject(options)}`;
	await httpDelete(endpoint);
}

export async function muteUser(request: MuteUserRequest) {
	const endpoint = `${chatAdminEndpoint}/MuteUser`;
	await post<boolean>(endpoint, request);
}

export async function unmuteUser(request: { chatRoomId: number; userId: number }) {
	const endpoint = `${chatAdminEndpoint}/UnmuteUser`;
	await post<boolean>(endpoint, request);
}

export async function banUser(request: BanUserRequest) {
	const endpoint = `${chatAdminEndpoint}/BanUser`;
	await post<boolean>(endpoint, request);
}

export async function unbanUser(request: { chatRoomId: number; userId: number }) {
	const endpoint = `${chatAdminEndpoint}/UnbanUser`;
	await post<boolean>(endpoint, request);
}

export async function promoteUser(request: PromoteUserRequest) {
	const endpoint = `${chatAdminEndpoint}/PromoteUser`;
	await post<boolean>(endpoint, request);
}

// User endpoints
export async function getAvailableChatRooms(objectType?: ObjectType, objectId?: number) {
	const endpoint = `${chatEndpoint}/GetAvailableChatRooms?${getQSFromObject({ objectType, objectId })}`;
	const result = await get<ChatRoom[]>(endpoint);
	return chatRoomArrayMapper(result);
}

export async function getNotDeliveredMessagesCountForUser() {
	const endpoint = `${chatEndpoint}/GetNotDeliveredMessagesCountForUser`;
	const result = await get<ChatNotDeliveredMessagesCount[]>(endpoint);
	return result;
}

export async function getLinkedObjectsWChatRoomsForUser() {
	const endpoint = `${chatEndpoint}/GetLinkedObjectsWChatRoomsForUser`;
	const result = await get<BizObject[]>(endpoint);
	return result;
}

export async function getChatUsers(objectType?: ObjectType, objectId?: number) {
	const endpoint = `${chatEndpoint}/GetChatUsers?${getQSFromObject({ objectType, objectId })}`;
	const result = await get<ChatUser[]>(endpoint);
	return result;
}

export async function getChatRoomUsers(options: { chatRoomId: number }) {
	const endpoint = `${chatEndpoint}/GetChatRoomUsers?${getQSFromObject(options)}`;
	const result = await get<ChatUser[]>(endpoint);
	return result ?? [];
}

export async function joinChatRoom(request: { chatRoomId: number }) {
	const endpoint = `${chatEndpoint}/JoinChatRoom`;
	const result = await post<ChatRoomMember>(endpoint, request);
	return mapChatRoomMember(result || null);
}

export async function leaveChatRoom(request: { chatRoomId: number }) {
	const endpoint = `${chatEndpoint}/LeaveChatRoom`;
	await post<boolean>(endpoint, request);
}

export async function createDirectMessage(targetUserId: number, objectType?: ObjectType, objectId?: number) {
	const endpoint = `${chatEndpoint}/CreateDirectMessage`;
	const result = await post<ChatRoom>(endpoint, { targetUserId, objectType, objectId });
	return mapChatRoom(result || null);
}

export async function getUserDirectMessages() {
	const endpoint = `${chatEndpoint}/GetUserDirectMessages`;
	const result = await get<ChatRoom[]>(endpoint);
	return chatRoomArrayMapper(result);
}
